<template>
  <div>
    <v-card>
      <v-card-title>
        <v-icon color="primary"> mdi-bus-clock </v-icon>
        <span class="pl-2">Trips</span>

        <v-spacer></v-spacer>

        <v-sheet elevation="3" rounded="lg" class="text-center mx-auto">
          <div class="mx-4">
            <!-- Shift filter -->
            <v-radio-group v-model="trip_type" row>
              <v-radio label="All trips" value="all"></v-radio>

              <!-- Morning = orange darken-2 -->
              <v-radio
                label="Morning trips"
                value="morning"
                color="orange darken-2"
              ></v-radio>

              <!-- Afternoon = blue darken-1 -->
              <v-radio
                label="Afternoon trips"
                value="afternoon"
                color="blue darken-1"
              ></v-radio>

              <!-- Evening = purple darken-2 -->
              <v-radio
                label="Evening trips"
                value="evening"
                color="purple darken-2"
              ></v-radio>
            </v-radio-group>

            <!-- Tiny legend -->
            <v-row class="px-4 pb-2" align="center" no-gutters>
              <v-chip small :color="shiftColorMap[1]" dark class="ma-1">Morning</v-chip>
              <v-chip small :color="shiftColorMap[2]" dark class="ma-1">Afternoon</v-chip>
              <v-chip small :color="shiftColorMap[3]" dark class="ma-1">Evening</v-chip>
            </v-row>
          </div>
        </v-sheet>

        <v-spacer></v-spacer>

        <create-button @create="createTrip"></create-button>
        <activation-tool-tip model="trips"></activation-tool-tip>
      </v-card-title>

      <!-- tabs -->
      <v-tabs v-model="active_tab" show-arrows class="my-2">
        <v-tab v-for="tab in tabs" :key="tab.idx">
          <v-icon size="20" class="me-3">
            {{ tab.icon }}
          </v-icon>
          <span>{{ tab.title }}</span>
        </v-tab>
      </v-tabs>

      <!-- tabs item -->
      <v-tabs-items v-model="active_tab">
        <!-- active -->
        <v-tab-item>
          <trips-table
            :loading="isLoading"
            :trips="displayedActiveTrips"
            :mode="1"
            :shift-color-map="shiftColorMap"
            @trashRestoreTrip="trashRestoreTrip"
          />
        </v-tab-item>

        <!-- suspended -->
        <v-tab-item>
          <trips-table
            :loading="isLoading"
            :trips="displayedSuspendedTrips"
            :mode="2"
            :shift-color-map="shiftColorMap"
            @deleteSuspension="deleteSuspension"
          />
        </v-tab-item>

        <!-- trashed -->
        <v-tab-item>
          <trips-table
            :loading="isLoading"
            :trips="displayedTrashedTrips"
            :mode="3"
            :shift-color-map="shiftColorMap"
            @trashRestoreTrip="trashRestoreTrip"
          />
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import tripsTable from './trips-table.vue';
import EventBus from './eventBus';

import {
  mdiStopCircleOutline,
  mdiPlayCircleOutline,
  mdiTrashCan,
  mdiDeleteRestore,
  mdiAirplane,
  mdiMotionPause
} from "@mdi/js";

import ActivationToolTip from "@/components/ActivationToolTip";
import CreateButton from "@/components/CreateButton";
import auth from "@/services/AuthService";

export default {
  components: {
    tripsTable,
    ActivationToolTip,
    CreateButton,
  },
  data() {
    return {
      trip_type: 'all',
      activeTrips: [],
      trashedTrips: [],
      suspendedTrips: [],

      displayedActiveTrips: [],
      displayedTrashedTrips: [],
      displayedSuspendedTrips: [],

      isLoading: false,
      search: "",
      tabs: [
        { idx: 0, title: "Active",    icon: mdiAirplane },
        { idx: 1, title: "Suspended", icon: mdiMotionPause },
        { idx: 2, title: "Trashed",   icon: mdiTrashCan },
      ],
      active_tab: null,
      statuses: [
        { value: "Active",    color: "success" },
        { value: "Pending",   color: "warning" },
        { value: "Suspended", color: "error" },
      ],
      icons: {
        mdiStopCircleOutline,
        mdiPlayCircleOutline,
        mdiTrashCan,
        mdiDeleteRestore,
        mdiAirplane
      },

      // shift type color map
      // 1=morning, 2=afternoon, 3=evening
      shiftColorMap: {
        1: 'orange darken-2', // morning
        2: 'blue darken-1',   // afternoon
        3: 'purple darken-2', // evening
      },
    };
  },
  watch: {
    active_tab(newVal) {
      localStorage.tabIdxTrips = newVal;
    },
    trip_type(newVal) {
      this.filterTrips(newVal);
    }
  },
  mounted() {
    this.active_tab = parseInt(localStorage.tabIdxTrips);
    this.loadTrips();
  },
  created () {
    EventBus.$on('DELETE_SUSPENSION', (suspension, index) => {
      this.deleteSuspension(suspension, index)
    });
  },
  methods: {
    tConvert(time) {
      if (time == null) return null;
      time = time.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)/) || [time];
      if (time.length > 1) {
        time = time.slice(1);
        time[5] = +time[0] < 12 ? " AM" : " PM";
        time[0] = +time[0] % 12 || 12;
      }
      return time.join("");
    },
    getStatusColor(status) {
      return this.statuses[status - 1].color;
    },
    getStatusValue(status) {
      return this.statuses[status - 1].value;
    },
    getShiftColor(shiftType) {
      return this.shiftColorMap[shiftType] || 'grey';
    },
    displayRoute(route_id) {
      this.$router.push({ name: "view-route", params: { route_id } });
    },

    filterTrips(trips_type) {
      this.trip_type = trips_type;

      if (trips_type === 'all') {
        this.displayedActiveTrips    = this.activeTrips;
        this.displayedTrashedTrips   = this.trashedTrips;
        this.displayedSuspendedTrips = this.suspendedTrips;
        return;
      }

      const toCode = (t) =>
        t === 'morning'   ? 1 :
        t === 'afternoon' ? 2 :
        t === 'evening'   ? 3 : null;

      const code = toCode(trips_type);

      // Active & trashed arrays are flat objects with route.shift_type
      this.displayedActiveTrips  = this.activeTrips.filter(trip => trip?.route?.shift_type === code);
      this.displayedTrashedTrips = this.trashedTrips.filter(trip => trip?.route?.shift_type === code);

      // Suspended items often come nested like { trip: { route: { shift_type } } }
      this.displayedSuspendedTrips = this.suspendedTrips.filter(susp =>
        (susp?.trip?.route?.shift_type === code) || (susp?.route?.shift_type === code)
      );
    },

    deleteSuspension(suspension_id, index) {
      this.$swal
        .fire({
          title: "Remove suspension",
          text: "Are you sure to remove this suspension?",
          icon: "success",
          showCancelButton: true,
          confirmButtonText: "Yes",
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.deleteSuspensionServer(suspension_id, index);
          }
        });
    },

    deleteSuspensionServer(suspension_id, index) {
      this.isSubmit = true;
      axios
        .delete(`/trips/remove-suspension/${suspension_id}`)
        .then(() => {
          this.isSubmit = false;
          if (index != null) {
            this.displayedSuspendedTrips.splice(index, 1);
          }
          this.$notify({ title: "Success", text: "Suspension removed", type: "success" });
          if (index == null) this.$router.go(-1);
        })
        .catch(() => {
          this.isSubmit = false;
          this.$notify({ title: "Error", text: "Error", type: "error" });
        });
    },

    trashRestoreTrip(trip, index) {
      this.$swal
        .fire({
          title: (trip.status_id != 1 ? "Restore" : "Trash") + " trip",
          text: "Are you sure to " + (trip.status_id != 1 ? "restore" : "trash") + " this trip?",
          icon: trip.status_id != 1 ? "success" : "error",
          showCancelButton: true,
          confirmButtonText: "Yes",
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.trashRestoreTripServer(trip, index);
          }
        });
    },

    trashRestoreTripServer(trip, index) {
      this.isSubmit = true;
      axios
        .post("/trips/trash-restore", { trip_id: trip.id })
        .then(() => {
          this.isSubmit = false;

          if (trip.status_id == 1) {
            const idx = this.activeTrips.findIndex((t) => t.id == trip.id);
            if (idx > -1) {
              this.activeTrips[idx].status_id = 3;
              this.activeTrips.splice(idx, 1);
            }
            trip.status_id = 3;
            this.trashedTrips.push(trip);
          } else {
            const idx = this.trashedTrips.findIndex((t) => t.id == trip.id);
            if (idx > -1) {
              this.trashedTrips[idx].status_id = 1;
              this.trashedTrips.splice(idx, 1);
            }
            trip.status_id = 1;
            this.activeTrips.push(trip);
          }

          this.filterTrips(this.trip_type);

          this.$notify({
            title: "Success",
            text: "Trip " + (trip.status_id != 1 ? "trashed" : "restored"),
            type: "success",
          });
        })
        .catch(() => {
          this.isSubmit = false;
          this.$notify({ title: "Error", text: "Error", type: "error" });
        });
    },

    loadTrips() {
      this.isLoading = true;
      this.activeTrips = [];
      this.trashedTrips = [];
      this.suspendedTrips = [];

      axios
        .get(`/trips/all`)
        .then((response) => {
          this.activeTrips     = response.data.activeTrips || [];
          this.trashedTrips    = response.data.trashedTrips || [];
          this.suspendedTrips  = response.data.suspendedTrips || [];

          this.displayedActiveTrips    = this.activeTrips;
          this.displayedTrashedTrips   = this.trashedTrips;
          this.displayedSuspendedTrips = this.suspendedTrips;
        })
        .catch((error) => {
          this.$notify({ title: "Error", text: "Error while retrieving trips", type: "error" });
          auth.checkError(error?.response?.data?.message, this.$router, this.$swal);
        })
        .then(() => {
          this.isLoading = false;
        });
    },

    createTrip() {
      this.$router.push({ name: "create-trip" });
    },
  },
};
</script>
